import { NotificationIdentifier } from '../../types/notification-identifier';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { CompositeModel } from '../complex/composite.model';
import { TextModel } from '../primitive/text.model';

export class NotificationIdentifierModel extends CompositeModel {
    private ownedFrontId: TextModel;
    private ownedQmNum: TextModel;
    private ownedMaNum: TextModel;
    public constructor(frontId: string, qmNum: string, maNum: string) {
        super('NotificationIdentifierModel');

        this.ownedFrontId = <TextModel>this.set(new TextModel('FrontId', frontId));
        this.ownedQmNum = <TextModel>this.set(new TextModel('NotificationNo', qmNum));
        this.ownedMaNum = <TextModel>this.set(new TextModel('TaskNumber', maNum));
    }

    public get frontId(): string {
        return this.ownedFrontId.value;
    }

    public get qmNum(): string {
        return this.ownedQmNum.value;
    }

    public get maNum(): string {
        return this.ownedMaNum.value;
    }

    /**
     * Public methods, assigned to prototype
     */
    public getFrontId(): string {
        return this.frontId;
    }

    public getQmnum(): string {
        return this.qmNum;
    }

    public getManum(): string {
        return this.maNum;
    }

    public toString(): string {
        return this.frontId + ':' + this.qmNum + ':' + this.maNum;
    }

    public static fromString(id: string): NotificationIdentifierModel {
        const parts = id.split(':');
        const mustHaveLength = 3;

        if (parts.length !== mustHaveLength) {
            throw new Error(`Could not create notification identifier model from string '${id}'`);
        }

        const frontIdPosition = 0;
        const qmNumPosition = 1;
        const maNumPosition = 2;

        return new NotificationIdentifierModel(parts[frontIdPosition], parts[qmNumPosition], parts[maNumPosition]);
    }

    /**
     * Identity comparison over the three fields alone, so a caller holding the plain identifier — a
     * state shape, an action payload — can compare without building a model first. A model satisfies
     * the plain type, so every existing caller still fits.
     *
     * @param a
     * @param b
     */
    public static compare(a: NotificationIdentifier, b: NotificationIdentifier): boolean {
        return a.frontId === b.frontId && a.qmNum === b.qmNum && a.maNum === b.maNum;
    }

    public equalsTo(other: NotificationIdentifierModel): boolean {
        return NotificationIdentifierModel.compare(this, other);
    }

    public toJSON(): unknown {
        return {
            frontId: this.frontId,
            qmNum: this.qmNum,
            maNum: this.maNum,
        };
    }

    public getOutModel(): ODataNotificationIdentifierOutModel {
        return <ODataNotificationIdentifierOutModel>super.getOutModel();
    }
}

export class NotificationIdentifierModelFromJson extends NotificationIdentifierModel {
    public constructor(data: JsonNotificationIdentifierModel) {
        super(data.frontId, data.qmNum, data.maNum);
    }
}

export interface JsonNotificationIdentifierModel {
    frontId: string;
    qmNum: string;
    maNum: string;
}

export interface ODataNotificationIdentifierOutModel extends ODataObject {
    FrontId: string;
    NotificationNo: string;
    TaskNumber: string;
}
